// This pragma is required for the css utility to work
import * as React from 'react';

/** @jsx jsx */
import { css, type SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';

import { GreenSpinner, WhiteSpinner } from '~/components/Spinner';
import { HeightSize } from '~/constants/form';
import { colors, fontFamily, fontWeight } from '~/styles/constants';

type ButtonSize = 'large' | 'medium' | 'mediumLarge';

interface Dimensions {
  height?: number;
  width?: number;
}

const buttonSizeDimensions: { [key in ButtonSize]: Dimensions } = {
  large: {
    height: HeightSize.large,
  },
  medium: {
    height: HeightSize.medium,
  },
  mediumLarge: {
    height: HeightSize.mediumLarge,
  },
};

const baseButton = css`
  font-family: ${fontFamily};
  position: relative;
  display: inline-block;
  text-align: center;
  cursor: pointer;
  white-space: nowrap;
  vertical-align: middle;
  user-select: none;
  outline: none;
  border-radius: 3px;

  line-height: 20px;
  padding: 0 20px;
  font-size: 14px;
  font-weight: ${fontWeight.medium};

  align-items: center;

  :disabled {
    cursor: not-allowed;
  }
`;

const LoadingText = styled('span')`
  margin-right: 10px;
`;

interface StyledButtonProps {
  dimensions?: Dimensions;
  extraRounded?: boolean;
  fullWidth?: boolean;
  rounded?: boolean;
}

export const StyledButton = styled('button')<StyledButtonProps>`
  ${baseButton};

  ${(props) =>
    props.dimensions && props.dimensions.width ? `width: ${props.dimensions.width}px;` : ''}

  ${(props) =>
    props.dimensions && props.dimensions.height ? `height: ${props.dimensions.height}px;` : ''}

  ${(props) => (props.fullWidth ? 'width: 100%;' : '')};
`;

export interface ButtonProps {
  children?: React.ReactNode;
  className?: string;
  css?: SerializedStyles;
  dataAnalytics?: string; // heap event identifier
  dataE2E?: string;
  dataPendo?: string;
  dataTestid?: string;
  disabled?: boolean;
  extraRounded?: boolean;
  form?: string;
  fullWidth?: boolean;
  id?: string;
  isLoading?: boolean;
  loadingText?: string;
  /**
   * Called when Button is clicked
   */
  onClick?(event: React.MouseEvent<HTMLElement>): void;
  ref?: React.RefObject<HTMLButtonElement>;
  rounded?: boolean;
  size?: ButtonSize;
  tabIndex?: number;
  type?: 'button' | 'reset' | 'submit';
  useGreenSpinner?: boolean;
}

const Button = (props: ButtonProps) => {
  const {
    css,
    dataAnalytics,
    dataE2E,
    dataPendo,
    dataTestid,
    disabled,
    extraRounded,
    form,
    fullWidth,
    id,
    loadingText,
    onClick,
    rounded,
    size,
    tabIndex,
    type,
  } = props;

  const buttonProps = {
    css,
    disabled,
    form,
    id,
    onClick,
    tabIndex,
    type,
  };

  const Spinner = props.useGreenSpinner ? GreenSpinner : WhiteSpinner;
  const dimensions = size ? buttonSizeDimensions[size] : buttonSizeDimensions.medium;

  return (
    <StyledButton
      {...buttonProps}
      className={props.className}
      data-analytics={dataAnalytics}
      data-e2e={dataE2E}
      data-heap-event={dataAnalytics}
      data-pendo={dataPendo}
      data-testid={dataTestid}
      dimensions={dimensions}
      extraRounded={extraRounded}
      fullWidth={fullWidth}
      rounded={rounded}
    >
      {props.isLoading ? (
        <React.Fragment>
          {loadingText && <LoadingText>{loadingText}</LoadingText>}
          <Spinner size={20} />
        </React.Fragment>
      ) : (
        props.children
      )}
    </StyledButton>
  );
};

Button.defaultProps = {
  disabled: false,
  type: 'button',
} as Partial<ButtonProps>;

export const primaryCss = css`
  color: white;
  background-color: ${colors.primary};
  border: none;
  :hover,
  :focus {
    background-color: ${colors.primaryFocus};
    box-shadow:
      0px 2px 4px -1px rgba(0, 0, 0, 0.2),
      0px 4px 5px rgba(0, 0, 0, 0.14),
      0px 1px 10px rgba(0, 0, 0, 0.12);
  }

  :disabled {
    background-color: ${colors.forest700};
    color: ${colors.white};
    font-weight: normal;
    box-shadow: none;
    opacity: 0.5;
  }
`;

export const PrimaryButton = styled(Button)`
  ${primaryCss};
`;

const darkerPrimaryAlternative = '#174432';
const disabledPrimaryAlternative = '#246a4e';

const primaryAlternativeCss = css`
  color: ${colors.primary};
  background-color: transparent;
  border: none;
  font-weight: ${fontWeight.medium};

  :hover,
  :focus {
    background-color: ${colors.forest100};
  }

  :disabled {
    color: ${colors.gray700};
    background-color: ${colors.forest50};
    font-weight: normal;
  }
`;

export const PrimaryAlternativeButton = styled(Button)`
  ${primaryAlternativeCss};
`;

export const greyButton = css`
  color: ${colors.secondary};
  background-color: transparent;
  border: 1px solid ${colors.secondary};
  font-weight: normal;
`;

export const GreyPrimaryAlternativeButton = styled(Button)`
  ${greyButton};

  :hover,
  :focus {
    color: ${colors.secondary};
    border-color: ${colors.secondary};
    background-color: ${colors.gray100};
  }

  :disabled {
    color: ${colors.secondary};
    border-color: transparent;
    background-color: ${colors.gray200};
    box-shadow: none;
  }
`;

export const SmallPrimaryButton = styled(PrimaryButton)`
  font-size: 13px;
  font-weight: 400;
  height: 37px;
`;

export const DangerButton = styled(Button)`
  color: ${colors.dangerRed};
  background-color: white;
  border: none;

  :hover,
  :focus {
    color: ${colors.white};
    background-color: ${colors.ruby700};
    box-shadow:
      0px 2px 4px -1px rgba(0, 0, 0, 0.2),
      0px 4px 5px rgba(0, 0, 0, 0.14),
      0px 1px 10px rgba(0, 0, 0, 0.12);
  }

  :disabled {
    opacity: 0.5;
    -webkit-opacity: 0.5;
    box-shadow: none;
  }
`;

export const SecondaryButton = styled(Button)`
  color: ${colors.secondary};
  border: none;
  background-color: transparent;
`;

const primaryAlternativePaintedCss = css`
  color: white;
  background-color: ${colors.primaryAlternative};
  border: 1px solid ${colors.primaryAlternative};

  :hover {
    background-color: ${darkerPrimaryAlternative};
    border-color: ${darkerPrimaryAlternative};
  }

  :disabled {
    background-color: ${disabledPrimaryAlternative};
    border-color: ${disabledPrimaryAlternative};
    box-shadow: none;
  }

  :focus {
    box-shadow: 0 0 0 1px ${colors.primaryAlternative};
  }
`;

export const AlternativeButton = styled(Button)`
  ${primaryAlternativePaintedCss};
`;

export const PrimaryBlueButton = styled(PrimaryButton)`
  ${primaryAlternativePaintedCss};
`;

const primaryGreyPaintedCss = css`
  color: ${colors.gray800};
  background-color: transparent;
  border: 1px solid transparent;
  box-sizing: border-box;
  outline: none;

  :hover,
  :focus {
    background-color: ${colors.gray100};
    border-color: ${colors.gray100};
  }

  :disabled {
    color: ${colors.gray800};
    opacity: 0.5;
  }
`;

export const PrimaryGreyButton = styled(Button)`
  ${primaryGreyPaintedCss}
`;
